/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils\providerTemplates.tier2.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Calls Routing module.
────────────────────────────────────────────────────────────────*/


import type { ProviderTemplate } from './providerTemplates';

export const TIER2_PROVIDER_TEMPLATES: ProviderTemplate[] = [
    // Callcentric
    {
        id: 'callcentric',
        name: 'Callcentric',
        description: 'Budget-friendly VoIP provider',
        website: 'https://callcentric.com',
        fields: [
            { name: 'username', label: 'Account Number', type: 'text', required: true, placeholder: '17771234567' },
            { name: 'password', label: 'SIP Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'callcentric.com', register: true, registerTransport: 'udp', expireSeconds: 300, retrySeconds: 30, context: 'public' }
    },

    // SIP.US
    {
        id: 'sipus',
        name: 'SIP.US',
        description: 'Simple and affordable SIP trunking',
        website: 'https://sip.us',
        fields: [
            { name: 'username', label: 'Username', type: 'text', required: true },
            { name: 'password', label: 'Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'gw1.sip.us', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' }
    },

    // DIDlogic
    {
        id: 'didlogic',
        name: 'DIDlogic',
        description: 'Global DID numbers and SIP trunking',
        website: 'https://didlogic.com',
        fields: [
            { name: 'username', label: 'Account ID', type: 'text', required: true },
            { name: 'password', label: 'SIP Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.didlogic.com', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' }
    },

    // AVOXI
    {
        id: 'avoxi',
        name: 'AVOXI',
        description: 'Global virtual numbers and SIP',
        website: 'https://avoxi.com',
        fields: [
            { name: 'username', label: 'Account ID', type: 'text', required: true },
            { name: 'password', label: 'SIP Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.avoxi.com', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' }
    },

    // Voxbone
    {
        id: 'voxbone',
        name: 'Voxbone',
        description: 'International voice and messaging',
        website: 'https://voxbone.com',
        fields: [
            { name: 'username', label: 'Username', type: 'text', required: true },
            { name: 'password', label: 'Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.voxbone.com', register: true, registerTransport: 'tcp', expireSeconds: 600, retrySeconds: 30, context: 'public' }
    },

    // Vonage (Nexmo)
    {
        id: 'vonage',
        name: 'Vonage (Nexmo)',
        description: 'Enterprise APIs for voice',
        website: 'https://vonage.com',
        fields: [
            { name: 'username', label: 'API Key', type: 'text', required: true },
            { name: 'password', label: 'API Secret', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.nexmo.com', register: false, registerTransport: 'tls', expireSeconds: 3600, retrySeconds: 30, context: 'public' },
        notes: 'Uses IP authentication. Configure your IP in Vonage Dashboard.'
    },

    // Lumen
    {
        id: 'lumen',
        name: 'Lumen (CenturyLink)',
        description: 'Enterprise SIP trunking',
        website: 'https://lumen.com',
        fields: [
            { name: 'proxy', label: 'SIP Proxy', type: 'text', required: true, placeholder: 'Your assigned SIP proxy' },
            { name: 'username', label: 'Username', type: 'text', required: true },
            { name: 'password', label: 'Password', type: 'password', required: true }
        ],
        defaults: { proxy: '', register: true, registerTransport: 'tls', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Contact Lumen for your specific SIP proxy address.'
    },

    // Skyetel
    {
        id: 'skyetel',
        name: 'Skyetel',
        description: 'Competitive rates with IP-based authentication',
        website: 'https://skyetel.com',
        fields: [
            { name: 'fromUser', label: 'Caller ID / DID', type: 'text', required: false, placeholder: '+1NXXNXXXXXX', helpText: 'Your outbound caller ID number' }
        ],
        defaults: { proxy: 'na.skyetel.com', register: false, registerTransport: 'udp', expireSeconds: 800, retrySeconds: 30, context: 'public', callerIdInFrom: true },
        notes: 'Skyetel uses IP authentication. Add your PBX IP in the Skyetel portal. No SIP registration required.'
    },

    // VoTeleSys
    {
        id: 'votelesys',
        name: 'VoTeleSys',
        description: 'Enterprise SIP trunking with flexible authentication',
        website: 'https://voxtelesys.com',
        fields: [
            { name: 'proxy', label: 'SIP Server', type: 'text', required: true, placeholder: 'Your assigned SIP server IP', helpText: 'Provided by VoTeleSys' },
            { name: 'username', label: 'Username (optional)', type: 'text', required: false, placeholder: 'Leave empty for IP auth' },
            { name: 'password', label: 'Password (optional)', type: 'password', required: false, helpText: 'Leave empty for IP authentication' }
        ],
        defaults: { proxy: '', register: false, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'VoTeleSys supports IP authentication (recommended) or credential-based auth. Get your SIP Server IP from the VoTeleSys portal.'
    },

    // Commio
    {
        id: 'commio',
        name: 'Commio',
        description: 'Intelligent voice platform with smart routing',
        website: 'https://commio.com',
        fields: [
            { name: 'username', label: 'Account ID', type: 'text', required: true },
            { name: 'password', label: 'SIP Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.commio.com', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Get credentials from the Commio portal. Supports intelligent call routing and failover.'
    },

    // net2phone
    {
        id: 'net2phone',
        name: 'net2phone',
        description: 'Global UCaaS and SIP trunking provider',
        website: 'https://net2phone.com',
        fields: [
            { name: 'username', label: 'SIP Username', type: 'text', required: true },
            { name: 'password', label: 'SIP Password', type: 'password', required: true },
            {
                name: 'region', label: 'Region', type: 'select', required: true,
                options: [
                    { value: 'siptrunk.net2phone.com', label: 'US (Americas)' },
                    { value: 'siptrunkuk.net2phone.com', label: 'UK (Europe)' },
                    { value: 'siptrunkhk.net2phone.com', label: 'Hong Kong (Asia)' },
                    { value: 'siptrunkbr.net2phone.com', label: 'Brazil' },
                    { value: 'siptrunkpe.net2phone.com', label: 'Peru' }
                ]
            }
        ],
        defaults: { proxy: '{region}', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Get credentials from app.net2phone.com. Supports G711 and G729 codecs.'
    },

    // BulkVS
    {
        id: 'bulkvs',
        name: 'BulkVS',
        description: 'Wholesale SIP trunking with competitive rates',
        website: 'https://bulkvs.com',
        fields: [
            { name: 'fromUser', label: 'Caller ID / DID', type: 'text', required: false, placeholder: '+1NXXNXXXXXX', helpText: 'Your outbound caller ID' }
        ],
        defaults: { proxy: 'sip.bulkvs.com', register: false, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'BulkVS uses IP authentication. Add your PBX IP in the BulkVS portal. SRV record provides full redundancy.'
    },

    // QuestBlue
    {
        id: 'questblue',
        name: 'QuestBlue',
        description: 'Business VoIP and SIP trunking services',
        website: 'https://questblue.com',
        fields: [
            { name: 'username', label: 'Username (optional)', type: 'text', required: false, placeholder: 'Leave empty for IP auth' },
            { name: 'password', label: 'Password (optional)', type: 'password', required: false, helpText: 'Leave empty for IP authentication' }
        ],
        defaults: { proxy: 'sbc.questblue.com', register: false, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'QuestBlue supports IP authentication (recommended). Configure your trunk region in the QuestBlue portal.'
    },

    // DIDforSale
    {
        id: 'didforsale',
        name: 'DIDforSale',
        description: 'Affordable DIDs and SIP trunking',
        website: 'https://didforsale.com',
        fields: [
            { name: 'username', label: 'SIP Username', type: 'text', required: true },
            { name: 'password', label: 'SIP Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.didforsale.com', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Generate SIP credentials in the DIDforSale customer portal.'
    },

    // Sinch
    {
        id: 'sinch',
        name: 'Sinch',
        description: 'Elastic SIP Trunking with global coverage',
        website: 'https://sinch.com',
        fields: [
            { name: 'username', label: 'Account ID', type: 'text', required: true },
            { name: 'password', label: 'API Secret', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.sinch.com', register: true, registerTransport: 'tls', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Sinch offers elastic SIP trunking. Get credentials from the Sinch Dashboard.'
    },

    // ── Latin America ──────────────────────────────────────────────────────────

    // Telcel México — SIP Connect Empresas
    {
        id: 'telcel-mx',
        name: 'Telcel México (SIP Connect)',
        description: 'Troncal SIP empresarial de Telcel México — Servicio SIP Connect',
        website: 'https://empresas.telcel.com',
        fields: [
            {
                name: 'proxy',
                label: 'Proxy SIP',
                type: 'text',
                required: true,
                placeholder: 'sipproxy.telcel.com',
                helpText: 'Dirección del proxy SIP entregada por Telcel al contratar el servicio'
            },
            {
                name: 'username',
                label: 'Usuario SIP / Número DID',
                type: 'text',
                required: true,
                placeholder: '525512345678',
                helpText: 'Número de teléfono o usuario SIP asignado por Telcel (formato E.164 sin +)'
            },
            {
                name: 'password',
                label: 'Contraseña SIP',
                type: 'password',
                required: true
            }
        ],
        defaults: {
            proxy: '',
            register: true,
            registerTransport: 'udp',
            expireSeconds: 3600,
            retrySeconds: 30,
            context: 'public',
            callerIdInFrom: false
        },
        notes: 'Telcel no publica su proxy SIP. Contacta a tu ejecutivo de Telcel Empresas para obtener: dirección del proxy, usuario y contraseña. Codecs soportados: G.711 µ-law (PCMU), G.711 A-law (PCMA), G.729. Puerto estándar: 5060 UDP. DTMF: RFC 2833. Requiere IP fija o registro SIP con credenciales. Recomendado configurar QoS priorizando tráfico de voz (DSCP EF/46) con jitter < 100ms.'
    },

    // Custom

    {
        id: 'custom',
        name: 'Custom Provider',
        description: 'Manual configuration for any SIP provider',
        fields: [
            { name: 'proxy', label: 'SIP Proxy/Server', type: 'text', required: true, placeholder: 'sip.provider.com' },
            { name: 'username', label: 'Username', type: 'text', required: true },
            { name: 'password', label: 'Password', type: 'password', required: true },
            { name: 'realm', label: 'Realm (optional)', type: 'text', required: false, placeholder: 'Same as proxy if empty' },
            {
                name: 'transport', label: 'Transport', type: 'select', required: true,
                options: [
                    { value: 'udp', label: 'UDP' }, { value: 'tcp', label: 'TCP' }, { value: 'tls', label: 'TLS' }
                ]
            }
        ],
        defaults: { proxy: '', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' }
    },
];
